import * as React from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';

import { Permissions } from '@app/AppConstants';

import { appStore } from '@app/AppStore';
import { securityService } from '@app/Services/SecurityService';
import DateTimeService from '@app/Services/DateTimeService';
import AirportStore from '@app/Stores/AirportStore';
import PersonInfoStore from '@app/Pages/PersonInfo/PersonInfoStore';

import { IntranetBlock } from './Components/IntranetBlock';
import { PersonSettingsBlock } from './Components/PersonSettingsBlock';
import { PersonInfoBlock } from './Components/PersonInfoBlock';
import { IdentificationBlock } from './Components/IdentificationBlock';
import { LicenseBlock } from './Components/LicenseBlock';
import { EntryExitBlock } from '@app/Pages/PersonInfo/Components/EntryExitBlock';
import { Loading } from '@app/Components/Loading';
import { AzureAuth } from '@app/Components/AzureAuth';
import { PlaningBlock } from '@app/Pages/PersonInfo/Components/PlaningBlock';
import { ChangeLogDialog } from '@app/Pages/PersonInfo/Components/Dialogs/ChangeLogDialog';
import { PersonDeleteDialog } from '@app/Pages/PersonInfo/Components/Dialogs/PersonDeleteDialog';
import { modalService } from '@app/Components/Modal';

type DeletePersonCallback = () => void;
type SendPersonToMbCallback = (personId: string) => void;
type PersonInfoProps = {
    personId: string;
    personDepartment?: string;
    onDeletePersonHandler: DeletePersonCallback;
    onSendPersonToMbHandler: SendPersonToMbCallback;
};

@observer
export class PersonInfo extends React.Component<PersonInfoProps, {}> {
    private _store: PersonInfoStore;
    private _airportStore: AirportStore;
    
    constructor(props: PersonInfoProps) {
        super(props);
        this._store = new PersonInfoStore(props.personId);
        this._airportStore = new AirportStore(this._store.dataLoading);
    }

    componentDidUpdate(prevProps: Readonly<PersonInfoProps>): void {
        if(prevProps.personId !== this.props.personId){
            this._store.loadPersonInfo(this.props.personId);
        }
    }

    render() {
        const personInfo = this._store.personInfo;
        if (!personInfo) return null;
        const { onSendPersonToMbHandler} = this.props;
        const { hasPermission, permissionViewCss } = securityService;
        return (
            <>
                <div className={'content' + (hasPermission(Permissions.ViewPersonsList, !AzureAuth.isUserOnlyInCrewGroup) ? '' : '-without-sidebar')}>
                    <div className="person-info">
                        <Loading loading={this._store.dataLoading.isPending} />
                        <h2>{personInfo.code}, {personInfo.firstName} {personInfo.lastName}, {this._formatDate(personInfo.birthDate)}
                            {personInfo.isDeleted && <span className="badge badge-pill badge-danger">Deleted</span>}
                            {hasPermission(Permissions.ViewPersonLog, AzureAuth.isUserInAdminGroup) && <DefaultButton text={'Log'} onClick={this._onShowLogClick} className={securityService.permissionCss(Permissions.ViewPersonLog)}/>}
                            {hasPermission(Permissions.CanDeletePerson, AzureAuth.isUserInAdminGroup) && !personInfo.isDeleted && <DefaultButton text={'Delete Person'} onClick={this._onDeleteClick} className={securityService.permissionCss(Permissions.CanDeletePerson)} />}
                            {(appStore.isCtrlKey && appStore.isShiftKey && securityService.hasPermission(Permissions.CanSendToMessageBus, AzureAuth.isUserInAdminGroup)) && <DefaultButton text={'Send to MessageBus'} onClick={() => onSendPersonToMbHandler(personInfo.id)} className={securityService.permissionCss(Permissions.CanSendToMessageBus)} />}
                        </h2>
                        {personInfo.isDeleted && <h6 className="text-danger">Delete Comment: {personInfo.deleteComment}</h6>}                        
                        <h6><b>Person id:</b> {personInfo.id}</h6>
                        {securityService.enablePermissionsColoring && <h6>
                            State after enabling permissions (legend):
                            <span className="badge security security-visible">Secured, Visible and enabled</span>
                            <span className="badge security security-hidden">Secured, not visible</span>
                            <span className="badge security security-readonly">Secured, visible but read-only</span>
                        </h6>}

                        <div className="row">
                            {hasPermission(Permissions.ViewPersonInfoBlock) &&
                                <div className={'col-xl-6 col-md-12' + permissionViewCss(Permissions.ViewPersonInfoBlock)}>
                                    <PersonInfoBlock
                                        personInfo={personInfo}
                                        isValidField={this._store.isValidField}
                                        onChangeCheckboxHandler={this._store.onChangeCheckboxCallback}
                                        onChangeDateFieldHandler={this._store.onChangeDateFieldCallback}
                                        validateFormHandler={this._store.validateForm}
                                        uploadPersonImageHandler={this._store.uploadPersonImage}
                                    />
                                </div>
                            }
                            {hasPermission(Permissions.ViewIdentificationBlock) &&
                                <div className={'col-xl-6 col-md-12' + permissionViewCss(Permissions.ViewIdentificationBlock)}>
                                    <IdentificationBlock
                                        personInfo={personInfo}
                                        nationalities={this._store.nationalities}
                                        onChangeFieldHandler={this._store.onChangeFieldCallback}
                                    />
                                </div>
                            }
                        </div>
                        {hasPermission(Permissions.ViewEmploymentFunctionBlock) &&
                            <div className={'person-info-block' + permissionViewCss(Permissions.ViewEmploymentFunctionBlock)}>
                                <EntryExitBlock
                                    personId={personInfo.id}
                                    personDepartment={this.props.personDepartment}
                                />
                            </div>
                        }
                        {hasPermission(Permissions.ViewLicenseBlock) &&
                            <div className={'person-info-block' + permissionViewCss(Permissions.ViewLicenseBlock)}>
                                <LicenseBlock
                                    personInfo={this._store.personInfo}
                                    nationalities={this._store.nationalities}
                                    onChangeFieldHandler={this._store.onChangeFieldCallback}
                                    validateFormHandler={this._store.validateForm}
                                />
                            </div>
                        }
                        {hasPermission(Permissions.ViewPlanningBlock) &&
                            <div className={'person-info-block' + permissionViewCss(Permissions.ViewPlanningBlock)}>
                                <PlaningBlock
                                    airportStore={this._airportStore}
                                    personId={personInfo.id}
                                />
                            </div>
                        }
                        {hasPermission(Permissions.ViewIntranetBlock) &&
                            <div className={'row' + permissionViewCss(Permissions.ViewIntranetBlock)}>
                                <IntranetBlock
                                    personInfo={this._store.personInfo}
                                    onChangeCheckboxHandler={this._store.onChangeCheckboxCallback}
                                />
                            </div>
                        }
                        {hasPermission(Permissions.ViewPersonEnableNbu) &&
                            <div className={'row' + permissionViewCss(Permissions.ViewPersonEnableNbu)}>
                                <div className="col-xl-6 col-md-12 col-sm-12 person-info-block">
                                    <PersonSettingsBlock
                                        personInfo={this._store.personInfo}
                                        onChangeCheckboxHandler={this._store.onChangeCheckboxCallback}
                                    />
                                </div>
                            </div>
                        }
                        {!AzureAuth.isReadOnly && 
                        <div className="person-info-controls">
                            <DefaultButton onClick={this._store.cancel} text="Cancel" disabled={!this._store.isEnabled} />
                            &nbsp;
                            <PrimaryButton onClick={this._store.save} text="Save" disabled={!this._store.isFormValid || !this._store.isEnabled} />
                        </div>}
                    </div>
                </div>
            </>
        );
    }

    @action.bound
    private async _onShowLogClick(){
        const personInfo = this._store.personInfo;

        await modalService.show(ChangeLogDialog, {
            personId: personInfo.id
        }, {
            title: `Change log for ${personInfo.code}`
        });
    }

    @action.bound
    private async _onDeleteClick(){
        const personInfo = this._store.personInfo;
        await modalService.show(PersonDeleteDialog, {
            personId: personInfo.id,
            onDeleteHandler: this.props.onDeletePersonHandler
        }, {
            title: `Delete person ${personInfo.code}`
        });
    }
    
    private _formatDate = (date: Date | undefined): string => {
        return date ? DateTimeService.toUiDate(date) : '';
    };
}